// Feature page content — one entry per /features/:slug route.
// Add a new feature by adding an entry here; FeaturePage.jsx + the sitemap
// registry in backend/routes/seo.py are the only other places to update.

export const FEATURES = [
  {
    slug: "crm",
    icon: "Users",
    title: "Contractor CRM",
    metaTitle: "Contractor CRM Software | ServiceGuru Pro",
    metaDescription: "ServiceGuru Pro's contractor CRM keeps every customer, job, and sales opportunity organized in one place — built for HVAC, plumbing, electrical and other home service businesses.",
    primaryKeyword: "contractor CRM software",
    intro: "Stop chasing customer details across spreadsheets and text threads. ServiceGuru Pro's CRM keeps every customer's contact info, job history, equipment, and sales opportunities in one searchable record.",
    benefits: [
      { title: "One record per customer", desc: "Every job, note, estimate, invoice, and membership tied to a single customer profile — no more digging through old texts." },
      { title: "Built-in sales pipeline", desc: "Track leads and quoted jobs through stages from first contact to won, without a separate sales tool." },
      { title: "Searchable history", desc: "Find any customer by name, phone, address, or job number in seconds from the field or the office." },
      { title: "Team-wide visibility", desc: "Dispatchers, technicians, and office staff all see the same up-to-date customer record." },
    ],
    useCases: [
      "A dispatcher opens a customer's profile mid-call to confirm their last service date and equipment before booking a job.",
      "A technician on-site pulls up notes from the customer's last visit to avoid asking the same questions twice.",
      "An office admin follows up on a quoted job sitting in the sales pipeline for over a week.",
    ],
    faqs: [
      { question: "Is ServiceGuru Pro's CRM built specifically for home service businesses?", answer: "Yes — every field, from job history to equipment tracking, is designed around how HVAC, plumbing, electrical and similar trades actually work, not a generic sales CRM." },
      { question: "Can technicians see customer history in the field?", answer: "Yes, technicians can view a customer's job and service history from the mobile app before or during a visit." },
      { question: "Does the CRM connect to estimates and invoices?", answer: "Yes — estimates, invoices, and payments are all linked directly to the customer record automatically." },
    ],
    relatedFeatures: ["customer-management", "sales-pipeline", "dispatching"],
    relatedIndustries: ["hvac", "plumbing"],
  },
  {
    slug: "dispatching",
    icon: "CalendarRange",
    title: "Dispatching",
    metaTitle: "Field Service Dispatch Software | ServiceGuru Pro",
    metaDescription: "Drag-and-drop dispatch board for field service teams. Assign jobs to the right technician, see live status, and keep every truck moving with ServiceGuru Pro's dispatch software.",
    primaryKeyword: "field service dispatch software",
    intro: "A visual, drag-and-drop dispatch board built for contractor dispatch software — assign jobs to technicians, see live job status, and keep the day's schedule under control.",
    benefitsImage: "/dispatch-board-live-map.png",
    benefits: [
      { title: "Drag-and-drop assignment", desc: "Move unassigned jobs onto a technician's day with a single drag, no phone tag required." },
      { title: "Live job status", desc: "See every job's status — Scheduled, En Route, On Site, In Progress, Completed — update in real time." },
      { title: "GPS-aware routing", desc: "See technician locations alongside the schedule to assign the closest available tech to a new job." },
      { title: "Fewer double-bookings", desc: "A single shared board means dispatchers always see the same up-to-date schedule." },
    ],
    useCases: [
      "A same-day emergency call comes in and the dispatcher assigns it to the nearest available technician in one drag.",
      "A technician marks a job 'On Site' from their phone and the dispatch board updates instantly for the office.",
      "The dispatcher reshuffles the whole day's board after a job runs long, without calling every technician.",
    ],
    faqs: [
      { question: "Does ServiceGuru Pro include a dispatch board?", answer: "Yes, a full drag-and-drop dispatch board is included, showing every job and technician for the day." },
      { question: "Can I see where technicians are while dispatching?", answer: "Yes, GPS location and live job status are shown alongside the dispatch board." },
      { question: "Is dispatching available on mobile for technicians?", answer: "Technicians receive and update their assigned jobs from the ServiceGuru Pro mobile app." },
    ],
    relatedFeatures: ["scheduling", "gps-fleet-tracking", "job-management"],
    relatedIndustries: ["hvac", "plumbing"],
  },
  {
    slug: "job-management",
    icon: "Wrench",
    title: "Job Management",
    metaTitle: "Job Management Software for Contractors | ServiceGuru Pro",
    metaDescription: "Manage every job from first call to final invoice — scheduling, technician assignment, status tracking, notes, and photos — with ServiceGuru Pro's job management software.",
    primaryKeyword: "job management software for contractors",
    intro: "Every job — from the first phone call to the final invoice — lives in one record, so nothing falls through the cracks between the office and the field.",
    benefits: [
      { title: "Full job lifecycle", desc: "Track a job through Scheduled, En Route, On Site, In Progress, and Completed statuses." },
      { title: "Notes and photos on every job", desc: "Technicians log notes and photos directly on the job so the office always has context." },
      { title: "Linked to the customer record", desc: "Every job automatically appears on the customer's history for future reference." },
      { title: "One tap to estimate or invoice", desc: "Convert a completed job straight into an estimate or invoice without re-entering details." },
    ],
    useCases: [
      "A technician adds a photo and note to a job before leaving the site, so the office can bill accurately.",
      "An admin reviews a customer's full job history before scheduling a follow-up visit.",
      "A dispatcher reschedules a job and every linked estimate stays attached automatically.",
    ],
    faqs: [
      { question: "What job statuses does ServiceGuru Pro track?", answer: "Scheduled, En Route, On Site, In Progress, and Completed, with timestamps for each transition." },
      { question: "Can technicians add notes and photos to a job?", answer: "Yes, from the mobile app, notes and photos attach directly to the job record." },
      { question: "Do jobs connect to estimates and invoices automatically?", answer: "Yes, jobs can be converted directly into estimates and invoices without duplicate data entry." },
    ],
    relatedFeatures: ["dispatching", "scheduling", "estimates"],
    relatedIndustries: ["general-contractors", "roofing"],
  },
  {
    slug: "scheduling",
    icon: "CalendarRange",
    title: "Scheduling",
    metaTitle: "Home Service Scheduling Software | ServiceGuru Pro",
    metaDescription: "Book appointments, manage technician availability, and keep your calendar full with ServiceGuru Pro's home service scheduling software.",
    primaryKeyword: "home service scheduling software",
    intro: "A shared calendar and dispatch board keep appointments, technician availability, and recurring service visits organized in one view.",
    benefits: [
      { title: "Shared team calendar", desc: "Every appointment is visible to dispatchers, technicians, and office staff in one calendar." },
      { title: "Technician availability", desc: "See who's free before booking a new appointment, avoiding overlaps." },
      { title: "Recurring appointments", desc: "Set up repeat visits for membership customers and recurring-service accounts." },
      { title: "Deep link to jobs and customers", desc: "Every calendar entry links straight to its job and customer record." },
    ],
    useCases: [
      "An office admin books a next available slot while a customer is still on the phone.",
      "A landscaping crew's recurring weekly visits populate automatically for the season.",
      "A dispatcher checks technician availability before promising a same-week appointment.",
    ],
    faqs: [
      { question: "Can I schedule recurring appointments?", answer: "Yes, recurring visits are supported for membership and repeat-service customers." },
      { question: "Does scheduling sync with the dispatch board?", answer: "Yes, the calendar and dispatch board share the same underlying job data." },
    ],
    relatedFeatures: ["dispatching", "job-management", "memberships"],
    relatedIndustries: ["landscaping", "pool-service"],
  },
  {
    slug: "estimates",
    icon: "FileText",
    title: "Estimates",
    metaTitle: "Estimate Software for Contractors | ServiceGuru Pro",
    metaDescription: "Build professional estimates from your pricebook, get customer e-signatures, and convert accepted estimates straight into invoices with ServiceGuru Pro.",
    primaryKeyword: "estimate software for contractors",
    intro: "Build an estimate from your pricebook in minutes, send it for e-signature, and convert an accepted estimate straight into an invoice.",
    benefits: [
      { title: "Pricebook-powered line items", desc: "Pull services and materials straight from your pricebook so pricing stays consistent." },
      { title: "Customer e-signature", desc: "Customers can review and accept estimates online, with an optional deposit collected at acceptance." },
      { title: "One-click convert to invoice", desc: "An accepted estimate becomes an invoice without re-typing a single line item." },
      { title: "Branded PDF and email delivery", desc: "Send a branded PDF estimate by email directly from the job." },
    ],
    useCases: [
      "A technician builds an estimate on-site from the pricebook and emails it before leaving the driveway.",
      "A customer reviews and signs an estimate from their phone, paying a deposit on the spot.",
      "An office admin converts an accepted estimate into an invoice with one click.",
    ],
    faqs: [
      { question: "Can customers sign estimates online?", answer: "Yes, estimates support online review, e-signature, and optional deposit collection." },
      { question: "Does ServiceGuru Pro handle estimates and invoices?", answer: "Yes, estimates convert directly into invoices, keeping every line item and customer detail intact." },
    ],
    relatedFeatures: ["invoicing", "pricebook", "online-payments"],
    relatedIndustries: ["roofing", "hvac"],
  },
  {
    slug: "invoicing",
    icon: "Receipt",
    title: "Invoicing",
    metaTitle: "Invoicing Software for Home Service Businesses | ServiceGuru Pro",
    metaDescription: "Send branded invoices, track payment status, and collect payment online with ServiceGuru Pro's invoicing software for contractors and home service businesses.",
    primaryKeyword: "invoicing software for contractors",
    intro: "Turn a completed job or accepted estimate into a branded invoice, send it by email or text, and collect payment online without chasing checks.",
    benefits: [
      { title: "Branded PDF invoices", desc: "Invoices are generated with your logo, business info, and accent color automatically." },
      { title: "Pay links included", desc: "Every emailed or texted invoice includes a link customers can use to pay online instantly." },
      { title: "Automatic status tracking", desc: "Invoices track Sent, Viewed, and Paid status so nothing gets lost." },
      { title: "AI follow-up on unpaid invoices", desc: "Outbound AI calling can follow up on overdue invoices automatically." },
    ],
    useCases: [
      "An invoice is emailed automatically the moment a job is marked complete.",
      "A customer pays an overdue invoice from a text-message pay link without calling the office.",
      "An admin reviews all unpaid invoices in one list at the end of the week.",
    ],
    faqs: [
      { question: "Can customers pay invoices online?", answer: "Yes, every invoice includes an online pay link powered by Stripe." },
      { question: "Does ServiceGuru Pro follow up on unpaid invoices automatically?", answer: "Yes, the AI voice agent can place automated collection calls on overdue invoices." },
    ],
    relatedFeatures: ["online-payments", "estimates", "ai-voice-agents"],
    relatedIndustries: ["general-contractors", "electrical"],
  },
  {
    slug: "online-payments",
    icon: "CreditCard",
    title: "Online Payments",
    metaTitle: "Online Payments for Contractors | ServiceGuru Pro",
    metaDescription: "Accept card payments in the field or online with a pay link on every invoice, powered by Stripe — built into ServiceGuru Pro's home service CRM.",
    primaryKeyword: "online payments for contractors",
    intro: "Accept card payments in the field or online. Every invoice carries a secure pay link, powered by Stripe, so customers can pay the moment they're ready.",
    benefits: [
      { title: "Pay link on every invoice", desc: "Customers can pay instantly from an emailed or texted invoice link." },
      { title: "In-person card processing", desc: "Technicians can process a card payment on-site when a job is completed." },
      { title: "Deposits on estimates", desc: "Collect a deposit at the moment a customer accepts an estimate." },
      { title: "Automatic payment reconciliation", desc: "Payments post directly to the invoice, keeping your books accurate without manual entry." },
    ],
    useCases: [
      "A technician taps to collect payment on a tablet the moment a job finishes.",
      "A customer pays a deposit while signing an estimate on their phone.",
      "An office admin sees a payment land on an invoice in real time.",
    ],
    faqs: [
      { question: "What payment processor does ServiceGuru Pro use?", answer: "Online and in-person payments are processed securely through Stripe." },
      { question: "Can I collect a deposit on an estimate?", answer: "Yes, deposits can be collected at the moment a customer accepts an estimate." },
    ],
    relatedFeatures: ["invoicing", "estimates", "memberships"],
    relatedIndustries: ["roofing", "general-contractors"],
  },
  {
    slug: "pricebook",
    icon: "BookOpen",
    title: "Pricebook",
    metaTitle: "Pricebook Software for Contractors | ServiceGuru Pro",
    metaDescription: "Keep every service and material price consistent across your team with ServiceGuru Pro's centralized pricebook — built for estimates, invoices, and commissions.",
    primaryKeyword: "pricebook software for contractors",
    intro: "A centralized catalog of services and materials keeps pricing consistent across every technician and every estimate.",
    benefits: [
      { title: "Consistent pricing", desc: "Every technician quotes from the same catalog, eliminating pricing guesswork." },
      { title: "Commission-ready", desc: "Attach a commission rate or piece rate to any pricebook item for automatic payroll calculation." },
      { title: "Fast estimate building", desc: "Add pricebook items to an estimate in a couple of taps instead of typing prices from memory." },
      { title: "Track material usage", desc: "Reporting shows which materials and services are used most, by revenue and quantity." },
    ],
    useCases: [
      "A technician adds three pricebook items to a same-day estimate without leaving the driveway.",
      "An admin updates a material's price once and it applies to every future estimate instantly.",
      "A manager reviews which services drive the most revenue using the pricebook report.",
    ],
    faqs: [
      { question: "Can pricebook items include a commission rate?", answer: "Yes, each item can carry a commission percentage or piece rate used by the payroll engine." },
    ],
    relatedFeatures: ["estimates", "invoicing", "payroll"],
    relatedIndustries: ["hvac", "plumbing"],
  },
  {
    slug: "memberships",
    icon: "ShieldCheck",
    title: "Memberships",
    metaTitle: "Membership & Service Plan Software | ServiceGuru Pro",
    metaDescription: "Sell recurring membership and service plans, apply member pricing automatically, and renew on payment with ServiceGuru Pro's membership software.",
    primaryKeyword: "service plan software for contractors",
    intro: "Sell recurring membership or service-agreement plans, apply member pricing automatically on jobs, and renew billing without manual follow-up.",
    benefits: [
      { title: "Recurring revenue", desc: "Turn one-time customers into recurring membership revenue with maintenance or service plans." },
      { title: "Automatic member pricing", desc: "Discounted member pricing applies automatically when a member's job is invoiced." },
      { title: "Renewal on payment", desc: "Memberships renew automatically when payment succeeds, with no manual tracking." },
      { title: "Visible on the customer record", desc: "Membership status is visible right on the customer's profile for every team member." },
    ],
    useCases: [
      "A pool service customer signs up for a seasonal maintenance plan billed automatically each month.",
      "An HVAC customer's tune-up visit is invoiced automatically at their member rate.",
      "An admin reviews which memberships are up for renewal this month.",
    ],
    faqs: [
      { question: "Do memberships auto-renew?", answer: "Yes, memberships renew automatically on successful payment." },
      { question: "Is member pricing applied automatically?", answer: "Yes, member discount pricing applies automatically to jobs tied to an active membership." },
    ],
    relatedFeatures: ["invoicing", "scheduling", "online-payments"],
    relatedIndustries: ["pool-service", "landscaping"],
  },
  {
    slug: "marketing-automation",
    icon: "Megaphone",
    title: "Marketing Automation",
    metaTitle: "Contractor Marketing Automation Software | ServiceGuru Pro",
    metaDescription: "Automate review requests, follow-up campaigns, and AI-designed email marketing with ServiceGuru Pro's contractor marketing automation software.",
    primaryKeyword: "contractor marketing automation",
    intro: "Automated email and SMS follow-ups keep past customers coming back — review requests, re-engagement campaigns, and AI-designed marketing emails, all triggered automatically.",
    benefits: [
      { title: "Review request automation", desc: "Automatically request a Google review after a job is marked complete." },
      { title: "Merge-tag personalization", desc: "Campaigns pull real customer, job, and invoice data into every message automatically." },
      { title: "AI-designed emails", desc: "Generate on-brand marketing emails with AI-designed images in seconds." },
      { title: "Trigger-based sending", desc: "Campaigns fire automatically on events like job completion or membership renewal." },
    ],
    useCases: [
      "A review request text goes out automatically two hours after a job is completed.",
      "A win-back email campaign targets customers who haven't booked in over a year.",
      "A marketing email is generated with an AI-designed header image in under a minute.",
    ],
    faqs: [
      { question: "Can review requests be sent automatically?", answer: "Yes, a review request can be triggered automatically after a job is completed." },
      { question: "Does marketing automation support personalization?", answer: "Yes, merge tags pull in real customer, appointment, and invoice details automatically." },
    ],
    relatedFeatures: ["reputation-management", "automations", "shared-inbox", "customer-management"],
    relatedIndustries: ["home-service", "landscaping"],
  },
  {
    slug: "ai-assistant",
    icon: "Mic",
    title: "AI Voice Assistant",
    metaTitle: "AI CRM Assistant for Contractors | ServiceGuru Pro",
    metaDescription: "Guru, ServiceGuru Pro's voice-controlled AI assistant, lets your team create tasks, book appointments, and pull up job info hands-free — a true AI CRM for contractors.",
    primaryKeyword: "AI CRM for contractors",
    intro: "Guru is ServiceGuru Pro's hands-free voice assistant — create tasks, book appointments, check today's schedule, or pull up a customer's info by talking instead of typing.",
    benefits: [
      { title: "Hands-free task creation", desc: "Create tasks and calendar events by speaking, ideal for technicians who can't stop to type." },
      { title: "Voice-driven lookups", desc: "Ask for today's appointments, overdue jobs, or unpaid invoices and get an instant answer." },
      { title: "Natural conversation", desc: "Guru asks clarifying questions and remembers context across a conversation, not just single commands." },
      { title: "Works from any device", desc: "Available from the desktop app and the mobile technician app." },
    ],
    useCases: [
      "A technician driving between jobs asks Guru to add a task without touching their phone.",
      "A dispatcher asks Guru which jobs are overdue today and gets an instant spoken and visual answer.",
      "An office admin asks Guru to draft a new estimate by describing the job out loud.",
    ],
    faqs: [
      { question: "Does ServiceGuru Pro have an AI voice assistant?", answer: "Yes, the built-in Guru assistant lets your team create tasks, events, and estimates and look up job info by voice." },
      { question: "Can technicians use the AI assistant hands-free?", answer: "Yes, the assistant is designed to be used hands-free from the mobile app while in the field." },
    ],
    relatedFeatures: ["ai-diagnostic-assistant", "ai-voice-agents", "mobile-app"],
    relatedIndustries: ["hvac", "plumbing"],
  },
  {
    slug: "ai-diagnostic-assistant",
    icon: "Brain",
    title: "AI Diagnostic Assistant",
    metaTitle: "AI Diagnostic Assistant for HVAC & Service Technicians | ServiceGuru Pro",
    metaDescription: "ServiceGuru Pro's AI diagnostic assistant is trained on your own service manuals — helping technicians work through error codes and symptoms in real time.",
    primaryKeyword: "AI diagnostic assistant for technicians",
    intro: "Diagnostic Guru is an AI co-pilot trained on your own uploaded service manuals — it walks technicians through error codes, symptoms, and troubleshooting steps in real time.",
    benefits: [
      { title: "Trained on your manuals", desc: "Upload your own equipment manuals so answers reflect the exact models your team services." },
      { title: "Error code lookups", desc: "Technicians can describe an error code or symptom and get step-by-step guidance instantly." },
      { title: "Faster first-visit fixes", desc: "Less time spent calling a senior tech or searching PDFs on-site." },
      { title: "Available in the field", desc: "Accessible from the mobile app so technicians can use it standing in front of the equipment." },
    ],
    useCases: [
      "A junior HVAC technician describes an unfamiliar error code and gets a step-by-step troubleshooting flow.",
      "A technician uploads a photo of a nameplate to confirm which manual section applies.",
      "A service manager adds a new equipment manual so the assistant covers a newly serviced brand.",
    ],
    faqs: [
      { question: "How to diagnose HVAC problems using AI?", answer: "ServiceGuru Pro's diagnostic assistant lets a technician describe symptoms or an error code and returns guidance based on the manuals you've uploaded." },
      { question: "Is the diagnostic assistant specific to my equipment?", answer: "Yes, it's trained on the manuals your business uploads, not generic public information." },
    ],
    relatedFeatures: ["ai-knowledge-base", "ai-assistant", "job-management"],
    relatedIndustries: ["hvac", "electrical"],
  },
  {
    slug: "ai-voice-agents",
    icon: "PhoneCall",
    title: "AI Voice Agents",
    metaTitle: "AI Voice Agent for Home Service Businesses | ServiceGuru Pro",
    metaDescription: "Automated outbound AI calling for estimate follow-up, appointment reminders, and invoice collection — an AI voice agent built for home service businesses.",
    primaryKeyword: "AI voice agent for home service businesses",
    intro: "Outbound AI calling agents follow up on open estimates, remind customers of upcoming appointments, and collect on overdue invoices — automatically, without tying up your office team.",
    benefits: [
      { title: "Estimate follow-up calls", desc: "An AI agent calls customers with an open estimate to answer questions and close the sale." },
      { title: "Invoice collection calls", desc: "Automated calls follow up on overdue invoices with a pay link sent by text." },
      { title: "Appointment reminders", desc: "Reduce no-shows with automated reminder calls ahead of a scheduled visit." },
      { title: "Natural, on-brand voice", desc: "Choose from a catalog of natural voices so calls sound consistent with your brand." },
    ],
    useCases: [
      "An AI agent calls every customer with a week-old open estimate to ask if they have questions.",
      "A customer with an overdue invoice gets a courtesy call and a text with a pay link.",
      "A customer receives an automated reminder call the day before their scheduled appointment.",
    ],
    faqs: [
      { question: "Can AI agents call customers automatically?", answer: "Yes, outbound campaigns can call customers for estimate follow-up, invoice collection, or appointment reminders." },
      { question: "Do I need to write call scripts myself?", answer: "Campaigns come with sensible defaults and can be customized to match how your business talks to customers." },
    ],
    relatedFeatures: ["ai-receptionist", "estimates", "invoicing"],
    relatedIndustries: ["hvac", "roofing"],
  },
  {
    slug: "ai-receptionist",
    icon: "Headset",
    title: "AI Receptionist",
    metaTitle: "AI Receptionist for Contractors | ServiceGuru Pro",
    metaDescription: "Never miss another customer call. ServiceGuru Pro's AI receptionist answers every ring, books appointments, and handles common questions 24/7.",
    primaryKeyword: "AI receptionist for contractors",
    intro: "No missed calls, ever. The AI receptionist answers every inbound call, books appointments directly onto your schedule, and handles common customer questions — 24 hours a day.",
    benefits: [
      { title: "Answers every call", desc: "Calls are answered instantly, even after hours or during your busiest dispatching moments." },
      { title: "Books real appointments", desc: "Finds an open slot and books the appointment directly onto your live schedule." },
      { title: "Recognizes existing customers", desc: "Matches the caller to their existing customer record for a personalized conversation." },
      { title: "Handles FAQs automatically", desc: "Answers common questions about hours, service areas, and pricing without staff involvement." },
    ],
    useCases: [
      "A customer calls at 9pm and books a next-day appointment without ever speaking to a human.",
      "An existing customer calls in and the receptionist recognizes them and pulls up their history.",
      "A call comes in while every dispatcher is on another line, and it's still answered instantly.",
    ],
    faqs: [
      { question: "Does ServiceGuru Pro have an AI receptionist?", answer: "Yes, the AI receptionist answers inbound calls, books appointments, and handles common questions around the clock." },
      { question: "Can the AI receptionist book real appointments?", answer: "Yes, it checks live availability and books directly onto your schedule, not just taking a message." },
    ],
    relatedFeatures: ["ai-voice-agents", "shared-inbox", "scheduling"],
    relatedIndustries: ["home-service", "plumbing"],
  },
  {
    slug: "ai-knowledge-base",
    icon: "BookOpen",
    title: "AI Knowledge Base",
    metaTitle: "AI Knowledge Base for Service Technicians | ServiceGuru Pro",
    metaDescription: "Upload service manuals and documentation so your AI diagnostic assistant gives technicians accurate, model-specific answers — ServiceGuru Pro's AI knowledge base.",
    primaryKeyword: "AI knowledge base for technicians",
    intro: "Upload your own service manuals and documentation to build a searchable knowledge base that powers accurate, model-specific answers for your diagnostic AI assistant.",
    benefits: [
      { title: "Your own documentation", desc: "Upload manuals for the exact equipment brands and models your team actually services." },
      { title: "Searchable by your whole team", desc: "Technicians and office staff can search the knowledge base directly, not just through the AI." },
      { title: "Keeps answers accurate", desc: "Answers are grounded in your uploaded documentation rather than generic public information." },
      { title: "Grows over time", desc: "Add new manuals any time your business starts servicing a new brand or model." },
    ],
    useCases: [
      "A service manager uploads a new manufacturer's manual after adding a new equipment line.",
      "A technician searches the knowledge base directly for a wiring diagram before a visit.",
    ],
    faqs: [
      { question: "Can I upload my own manuals?", answer: "Yes, you can upload your own service manuals and documentation to build a company-specific knowledge base." },
    ],
    relatedFeatures: ["ai-diagnostic-assistant", "ai-assistant"],
    relatedIndustries: ["hvac", "electrical"],
  },
  {
    slug: "mobile-app",
    icon: "Smartphone",
    title: "Mobile App",
    metaTitle: "Field Service Mobile App | ServiceGuru Pro",
    metaDescription: "ServiceGuru Pro's installable mobile app gives technicians their schedule, job details, GPS clock-in, and AI assistant — right from their phone.",
    primaryKeyword: "field service mobile app",
    intro: "An installable mobile app puts a technician's full day — jobs, schedule, tasks, GPS clock-in, and the AI voice assistant — in their pocket.",
    benefits: [
      { title: "Full job detail in the field", desc: "Technicians see customer history, notes, and job details without calling the office." },
      { title: "GPS-verified clock-in", desc: "Time tracking uses GPS to verify a technician is on-site when clocking in." },
      { title: "Works like a native app", desc: "Installable as a Progressive Web App, so it behaves like a native app without an app-store download." },
      { title: "Voice assistant built in", desc: "The Guru voice assistant is available hands-free right from the mobile app." },
    ],
    useCases: [
      "A technician installs the app once on their phone's home screen and uses it every day like a native app.",
      "A technician clocks in on-site and the GPS timestamp verifies the location automatically.",
    ],
    faqs: [
      { question: "Can technicians use ServiceGuru Pro on mobile?", answer: "Yes, technicians use an installable mobile app for their schedule, job details, time clock, and voice assistant." },
      { question: "Do technicians need to download anything from an app store?", answer: "No, it installs directly from the browser as a Progressive Web App." },
    ],
    relatedFeatures: ["gps-fleet-tracking", "time-tracking", "ai-assistant"],
    relatedIndustries: ["landscaping", "roofing"],
  },
  {
    slug: "gps-fleet-tracking",
    icon: "MapPin",
    title: "GPS Fleet Tracking",
    metaTitle: "GPS Fleet Tracking for Field Service Technicians | ServiceGuru Pro",
    metaDescription: "See every technician live on the map with ServiceGuru Pro's GPS fleet tracking — arrival logs, geofenced clock-in, and accurate customer ETAs.",
    primaryKeyword: "GPS tracking for field service technicians",
    intro: "See every technician live on the map, with automatic arrival and departure logs and accurate ETAs — built into dispatching, not a separate tracking app.",
    benefitsImage: "/dispatch-board-live-map.png",
    benefits: [
      { title: "Live technician map", desc: "See exactly where every technician is right now from the dispatch board or reports." },
      { title: "Automatic arrival logs", desc: "Arrival and departure timestamps are logged automatically at each job site." },
      { title: "GPS-verified time tracking", desc: "Clock-in and clock-out are cross-checked against GPS location for accurate payroll hours." },
      { title: "Accurate customer ETAs", desc: "Live location data powers accurate on-my-way notifications to customers." },
    ],
    useCases: [
      "A dispatcher sees which technician is physically closest before assigning an emergency job.",
      "A manager reviews a technician's arrival and departure times for a disputed job.",
      "GPS and clock-in hours are compared automatically to flag a payroll discrepancy.",
    ],
    faqs: [
      { question: "Can ServiceGuru Pro track technician locations?", answer: "Yes, GPS location, arrival logs, and route history are tracked and visible to dispatchers and admins." },
    ],
    relatedFeatures: ["dispatching", "time-tracking", "mobile-app"],
    relatedIndustries: ["landscaping", "pool-service"],
  },
  {
    slug: "time-tracking",
    icon: "Clock",
    title: "Time Tracking",
    metaTitle: "Time Clock Software for Field Service Teams | ServiceGuru Pro",
    metaDescription: "GPS-verified clock in/out for field technicians, feeding straight into payroll — ServiceGuru Pro's time tracking software for home service businesses.",
    primaryKeyword: "time clock software for field service teams",
    intro: "Technicians clock in and out from the field, verified by GPS, with hours flowing directly into payroll — no separate timesheet app required.",
    benefits: [
      { title: "One-tap clock in/out", desc: "Technicians clock in and out from the mobile app in seconds." },
      { title: "GPS verification", desc: "Clock-in location is checked against the job site for accountability." },
      { title: "Feeds payroll automatically", desc: "Tracked hours flow straight into the payroll engine, with no re-entry." },
      { title: "Missing clock-out detection", desc: "Managers are flagged automatically when a technician forgets to clock out." },
    ],
    useCases: [
      "A technician clocks in on arrival and the system logs the GPS-verified timestamp automatically.",
      "A payroll accountant reviews a list of missing clock-outs before running payroll.",
    ],
    faqs: [
      { question: "Does time tracking feed directly into payroll?", answer: "Yes, clocked hours flow directly into the payroll engine without manual re-entry." },
    ],
    relatedFeatures: ["gps-fleet-tracking", "payroll", "mobile-app"],
    relatedIndustries: ["landscaping", "general-contractors"],
  },
  {
    slug: "payroll",
    icon: "Wallet",
    title: "Payroll",
    metaTitle: "Payroll Software for Home Service Businesses | ServiceGuru Pro",
    metaDescription: "Run payroll for hourly, salary, commission, and piece-rate technicians — with GPS-verified hours and pay stubs — using ServiceGuru Pro's payroll software.",
    primaryKeyword: "payroll software for home service businesses",
    intro: "Run payroll for hourly, salary, commission, and piece-rate technicians in one place — with GPS-verified hours, commission calculations, and branded pay stubs.",
    benefits: [
      { title: "Multiple pay models", desc: "Supports hourly, salary, commission, piece-rate, bonuses, and overtime rules per technician." },
      { title: "GPS-verified hours", desc: "Regular and overtime hours are calculated from GPS-verified clock times." },
      { title: "Automatic commission math", desc: "Commission is calculated automatically from completed jobs and pricebook rates." },
      { title: "Pay stub delivery", desc: "Branded pay stubs are generated and emailed directly to each technician." },
    ],
    useCases: [
      "An accountant reviews a payroll period's exceptions before locking it for the pay run.",
      "A technician's commission is calculated automatically from their completed jobs for the period.",
      "A pay stub PDF is emailed directly to a technician after payroll is finalized.",
    ],
    faqs: [
      { question: "Can ServiceGuru Pro handle commission-based pay?", answer: "Yes, commission, piece-rate, hourly, and salary pay models are all supported per technician." },
      { question: "Are pay stubs generated automatically?", answer: "Yes, branded pay stub PDFs can be generated and emailed to each technician." },
    ],
    relatedFeatures: ["time-tracking", "pricebook", "reports"],
    relatedIndustries: ["general-contractors", "electrical"],
  },
  {
    slug: "team-messaging",
    icon: "MessageSquare",
    title: "Team Messaging",
    metaTitle: "Team Messaging for Field Service Teams | ServiceGuru Pro",
    metaDescription: "Chat with dispatchers, technicians, and office staff in one place — ServiceGuru Pro's built-in team messaging keeps the whole crew in sync.",
    primaryKeyword: "team messaging for field service businesses",
    intro: "Built-in messaging keeps dispatchers, technicians, and office staff talking in one place — no separate group-text thread to manage.",
    benefits: [
      { title: "Direct and group chat", desc: "Message one teammate or a whole crew without leaving the platform." },
      { title: "Job context included", desc: "Conversations can reference a specific job so context isn't lost." },
      { title: "Available on mobile", desc: "Technicians chat from the mobile app while on the road." },
      { title: "Task reminders built in", desc: "Task assignments and reminders surface directly alongside team chat." },
    ],
    useCases: [
      "A dispatcher messages a technician a heads-up about a difficult customer before they arrive.",
      "A crew group chat coordinates who's picking up materials for the morning's jobs.",
    ],
    faqs: [
      { question: "Is team messaging built into the platform?", answer: "Yes, direct and group messaging between staff is built in, with no separate app required." },
    ],
    relatedFeatures: ["shared-inbox", "mobile-app", "automations"],
    relatedIndustries: ["general-contractors", "landscaping"],
  },
  {
    slug: "shared-inbox",
    icon: "Inbox",
    title: "Shared Inbox",
    metaTitle: "Shared Customer Inbox for Contractors | ServiceGuru Pro",
    metaDescription: "One shared inbox for customer texts and calls, so any team member can respond without missing context — ServiceGuru Pro's shared inbox for home service teams.",
    primaryKeyword: "shared customer inbox for contractors",
    intro: "Every customer text and call lands in one shared inbox, so any team member can respond with full context — no missed messages from a personal cell phone.",
    benefits: [
      { title: "One number for the business", desc: "Customer texts go to a shared business number instead of a technician's personal phone." },
      { title: "Full conversation history", desc: "See the entire message thread with a customer, tied to their record." },
      { title: "Assign conversations", desc: "Route a conversation to the right team member so nothing gets missed." },
      { title: "Auto-compose from voice commands", desc: "The AI assistant can open a pre-filled reply to a customer by voice." },
    ],
    useCases: [
      "A customer texts a question and any available office staff member can respond with full context.",
      "A dispatcher reassigns an unanswered conversation to a teammate before it's missed.",
    ],
    faqs: [
      { question: "Do customer texts go to a shared number?", answer: "Yes, customer SMS conversations are handled through a shared business number and inbox, not personal phones." },
    ],
    relatedFeatures: ["team-messaging", "marketing-automation", "ai-receptionist"],
    relatedIndustries: ["home-service", "plumbing"],
  },
  {
    slug: "customer-management",
    icon: "Users",
    title: "Customer Management",
    metaTitle: "Customer Database Software for Contractors | ServiceGuru Pro",
    metaDescription: "Keep every customer's contact info, equipment, job history, and documents organized with ServiceGuru Pro's customer management software for contractors.",
    primaryKeyword: "customer database software for contractors",
    intro: "A complete record of every customer — contact info, equipment on file, appointment history, notes, and documents — always up to date and accessible to the whole team.",
    benefits: [
      { title: "Equipment on file", desc: "Track equipment make, model, and install date per customer for faster future service." },
      { title: "Full appointment history", desc: "Every past and upcoming appointment appears directly on the customer's profile." },
      { title: "Notes visible to the whole team", desc: "Any team member can see notes left by a technician or dispatcher." },
      { title: "Deep links to jobs and invoices", desc: "Jump from a customer record straight to any related job or invoice." },
    ],
    useCases: [
      "A technician checks a customer's equipment details before heading to a repair call.",
      "An office admin reviews a customer's full appointment and payment history before a follow-up call.",
    ],
    faqs: [
      { question: "Can I track customer equipment details?", answer: "Yes, equipment make, model, and service history can be tracked per customer." },
    ],
    relatedFeatures: ["crm", "job-management", "sales-pipeline"],
    relatedIndustries: ["hvac", "pool-service"],
  },
  {
    slug: "sales-pipeline",
    icon: "Kanban",
    title: "Sales Pipeline",
    metaTitle: "Sales Pipeline Software for Contractors | ServiceGuru Pro",
    metaDescription: "Track leads and quoted jobs from first contact to won with ServiceGuru Pro's Kanban-style sales pipeline, built for home service businesses.",
    primaryKeyword: "sales pipeline software for contractors",
    intro: "A Kanban-style pipeline tracks every lead and quoted job from first contact through won or lost, so no opportunity gets forgotten.",
    benefits: [
      { title: "Visual pipeline stages", desc: "Drag a lead card between stages as a deal progresses toward close." },
      { title: "Linked to estimates", desc: "Pipeline cards connect directly to the customer's estimates and job history." },
      { title: "Follow-up visibility", desc: "See at a glance which quoted jobs have gone quiet and need a nudge." },
      { title: "Assignable to reps", desc: "Assign pipeline cards to specific sales reps or account managers." },
    ],
    useCases: [
      "A sales rep drags a $12,000 re-roof quote from 'Quoted' to 'Won' after the customer signs.",
      "A manager reviews stalled pipeline cards that haven't moved in over two weeks.",
    ],
    faqs: [
      { question: "Is the sales pipeline separate from the CRM?", answer: "It's built into the same CRM, so pipeline cards share data with customer and job records automatically." },
    ],
    relatedFeatures: ["crm", "estimates", "customer-management"],
    relatedIndustries: ["roofing", "general-contractors"],
  },
  {
    slug: "reports",
    icon: "BarChart3",
    title: "Reports & Analytics",
    metaTitle: "Reporting Software for Home Service Businesses | ServiceGuru Pro",
    metaDescription: "Revenue, job, technician, and catalog reporting in one dashboard — ServiceGuru Pro's reporting software gives home service businesses a clear view of performance.",
    primaryKeyword: "reporting software for home service businesses",
    intro: "A single reports dashboard covers revenue, jobs completed, technician performance, and catalog usage — with date-range filtering and CSV export.",
    benefits: [
      { title: "Revenue over time", desc: "Track issued versus paid revenue with a comparison to the prior period." },
      { title: "Technician leaderboard", desc: "See jobs, revenue, average ticket, and on-time percentage per technician." },
      { title: "Catalog performance", desc: "Find your top services by revenue and top materials by quantity used." },
      { title: "Geo activity map", desc: "See every technician arrival plotted on a map for the selected date range." },
    ],
    useCases: [
      "A manager compares this month's revenue to last month before a team meeting.",
      "An owner reviews the technician leaderboard to decide on a quarterly bonus.",
    ],
    faqs: [
      { question: "Can I export reports?", answer: "Yes, revenue, jobs, technician, and catalog reports all support CSV export." },
    ],
    relatedFeatures: ["payroll", "sales-pipeline", "job-management"],
    relatedIndustries: ["general-contractors", "roofing"],
  },
  {
    slug: "automations",
    icon: "Workflow",
    title: "Automations",
    metaTitle: "Business Process Automation Software for Contractors | ServiceGuru Pro",
    metaDescription: "Trigger-and-action workflows that fire automatically on events like a job status change or an unpaid invoice — ServiceGuru Pro's automation engine for contractors.",
    primaryKeyword: "business process automation software for contractors",
    intro: "Trigger-and-action workflows fire automatically on real events in your business — a job status change, a new lead, or an unpaid invoice — without anyone lifting a finger.",
    benefits: [
      { title: "Event-based triggers", desc: "Build a workflow that fires the moment a job status changes or an invoice goes unpaid." },
      { title: "Multi-step actions", desc: "Chain multiple actions — send a text, notify a manager, move a pipeline card — from a single trigger." },
      { title: "No code required", desc: "Workflows are built visually, with no developer or code needed." },
      { title: "Works alongside marketing automation", desc: "Internal operational workflows run alongside customer-facing marketing campaigns." },
    ],
    useCases: [
      "A workflow notifies a dispatcher automatically whenever a job sits unassigned for over an hour.",
      "An unpaid invoice automatically triggers a reminder text three days after the due date.",
    ],
    faqs: [
      { question: "Do I need to know how to code to build an automation?", answer: "No, workflows are built visually from triggers and actions, no coding required." },
    ],
    relatedFeatures: ["marketing-automation", "job-management", "shared-inbox"],
    relatedIndustries: ["general-contractors", "home-service"],
  },
  {
    slug: "reputation-management",
    icon: "Star",
    title: "Reputation Management",
    metaTitle: "Reputation Management Software for Contractors | ServiceGuru Pro",
    metaDescription: "Turn happy customers into 5-star reviews and catch unhappy ones privately before they go public — ServiceGuru Pro's reputation management software for home service businesses.",
    primaryKeyword: "reputation management software for contractors",
    // Short teaser only — used on the /features index grid card. The full
    // story lives across the homepage section + this feature's own custom
    // page (pages/marketing/ReputationManagementPage.jsx), split so neither
    // repeats the other's copy.
    intro: "Catch unhappy customers privately before they post publicly, and turn happy ones into a steady stream of genuine 5-star reviews.",
    faqs: [
      { question: "How does ServiceGuru Pro help prevent bad public reviews?", answer: "Customers whose feedback signals a problem are flagged privately to the business owner instead of being posted to a public review site, so issues can be resolved before they ever become a public review." },
      { question: "Which review sites does it send happy customers to?", answer: "You set your own public review link (for example Google) and satisfied customers are encouraged to post there directly." },
      { question: "Does review outreach happen automatically?", answer: "Yes — ServiceGuru Pro automatically stays connected with customers after every job by text, email, and AI-powered calls, no manual follow-up required." },
      { question: "Can more positive reviews really offset a bad one?", answer: "Yes — building a larger volume of genuine positive reviews means an occasional negative review has significantly less impact on your overall star rating and reputation." },
      { question: "Do reviews affect my visibility in Google search?", answer: "Google considers review signals as part of local search prominence, so a steady history of genuine, recent reviews can help strengthen your business's visibility." },
    ],
    relatedFeatures: ["marketing-automation", "job-management", "crm"],
    relatedIndustries: ["hvac", "plumbing", "general-contractors"],
  },
];

export const FEATURES_BY_SLUG = Object.fromEntries(FEATURES.map((f) => [f.slug, f]));
